import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Label, Legend, Tooltip, XAxis, YAxis } from 'recharts';
import { debounce } from 'throttle-debounce';
import { usePrerequisites } from '../../../contexts/Prerequisites';
import util from '../../../util';

const CustomizedLabel = ({ x, y, fill, value, ...rest }) => (
	<text x={x} y={y} dx={12} dy={-7} textLength={value?.length} fontSize="11" fill={fill} textAnchor="middle" {...rest}>
		{value}
	</text>
);
const ColumnChart = forwardRef(
	(
		{
			data,
			hideLegend = true,
			label,
			labelProps,
			legendProps,
			width = '100%',
			height = 500,
			barSize = 24,
			showTooltip = true,
			xAxisLabel,
			yAxisLabel,
			onClick,
			...rest
		},
		ref
	) => {
		const theme = useTheme();

		const wrapperRef = useRef()
		const dataset = useMemo(() => Array.isArray(data)
			? data.sort((a, b) => (a?.label < b?.label ? -1 : a?.label > b?.label ? 1 : 0))
			: [], [data])
		const [{ strings }] = usePrerequisites();
		const handleOnClickBar = useCallback(
			(e, i) => {
				if (typeof onClick === 'function') {
					onClick(e, i);
				}
			},
			[onClick]
		);


		const tickFormatter = (value, index) => {
			const limit = 10;
			if (value?.length < limit) return value;

			return `${value?.substring?.(0, limit)}...`;
		};

		const totalWidth = 300 + (barSize * data.length * 1.5);
		const [actualWidth, setActualWidth] = useState(totalWidth);
		useEffect(() => {
			const wrapperWidth = wrapperRef.current?.clientWidth || 0;
			setActualWidth(Math.max(totalWidth, wrapperWidth));
			const applyEffect = debounce(350, () => {
				const wrapperWidth = wrapperRef.current?.clientWidth || 0;
				setActualWidth(Math.max(totalWidth, wrapperWidth));
			});

			window.addEventListener('beforeprint', applyEffect);
			window.addEventListener('resize', applyEffect);
			return () => {
				window.removeEventListener('resize', applyEffect);
				window.removeEventListener('beforeprint', applyEffect);
			};
		}, [totalWidth, wrapperRef.current]);

		return dataset.length > 0 ? (
			<Box
				sx={{
					overflowY: 'hidden',
					overflowX: 'auto',
					'& .recharts-wrapper': {
						margin: 'auto'
					}
				}}
				width={'100%'}
				ref={wrapperRef}
			>
				<BarChart
					data={dataset}
					margin={{ top: 50, right: 30, left: 30, bottom: 30 }}
					ref={ref}
					width={actualWidth}
					height={height}
					barSize={barSize}
					barCategoryGap={barSize}
				>
					<CartesianGrid strokeDasharray="3 3" />
					<Tooltip cursor />
					<XAxis
						interval={0}
						angle={270}
						dy={40}
						dx={-5}
						height={130}
						dataKey="label"
						tick={{ fontSize: 11, textAnchor: 'middle' }}
						label={
							<Label
								position="center"
								dy={50}
								fontSize={`11px`}
								fontWeight="bold"
								value={xAxisLabel}
								{...labelProps}
							/>
						}
						tickFormatter={tickFormatter}
					/>
					<YAxis
						type="number"
						label={
							<Label
								position="center"
								angle={270}
								dx={-30}
								fontSize={`11px`}
								color={theme.palette.text.primary}
								fontWeight="bold"
								value={yAxisLabel}
								{...labelProps}
							/>
						}
					/>
					{/* <Tooltip cursor /> */}
					<Bar
						dataKey={'value'}
						onClick={handleOnClickBar}
						fill={theme.palette.primary.main}
						label={CustomizedLabel}
						isAnimationActive={false}
					/>

					{!hideLegend && (
						<Legend
							wrapperStyle={{ height: '30%', overflowY: 'auto' }}
							verticalAlign="bottom"
						/>
					)}
				</BarChart>
			</Box>
		) : (
			<Box
				height={height}
				display="flex"
				alignItems="center"
				color="text.disabled"
				flexDirection={'column'}
				justifyContent="center"
			>
				<Typography variant="h1">
					<BarChartRoundedIcon fontSize="inherit" />
				</Typography>
				<Typography variant="h5">{strings.NoRecordFound || 'No Record found'}</Typography>
			</Box>
		);
	}
);

export default memo(ColumnChart, ({ data: prevData }, { data: nextData }) => !util.deepEqual(prevData, nextData));
